/**
 * De la hoja leída a las filas que se mandan — `OD-0057` D-02, D-05 y D-07.
 *
 * ```text
 * EMPAREJAR   cada cabecera del Excel con un campo de Faktelio. La minima
 *             exige la cabecera de la plantilla tal cual; la avanzada reconoce
 *             tambien los nombres habituales, y el usuario corrige el resto
 * FACTURAS    una fila por factura y tipo de IVA (D-05): las filas con el
 *             mismo numero y fecha son UNA factura con varias lineas
 * TROZOS      de 200 filas como mucho y de unos 200 KB, que es lo que la API
 *             admite por peticion
 * ERRORES     un CSV con las filas que no entraron y el motivo, para
 *             corregirlas y volver a subirlas
 * ```
 *
 * Funciones puras: no piden nada a la API ni leen el reloj. Las columnas
 * llegan de la API (`GET /api/v1/imports`), que es donde viven.
 */

import { posicion, type Hoja } from "./lector-hoja";

export type Columna = {
  readonly key: string;
  readonly header: string;
  readonly required: boolean;
  readonly line?: boolean;
  readonly aliases: readonly string[];
};

/** Para cada columna del Excel, el campo de Faktelio o `null` (no se importa). */
export type Emparejamiento = ReadonlyArray<string | null>;

export type Item = { readonly row: number; readonly data: Record<string, unknown> };

/** Minúsculas, sin tildes ni signos, espacios simples. */
export function normalizarCabecera(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9%/.]+/g, " ")
    .trim();
}

/**
 * Empareja las cabeceras. `minima`: sólo la cabecera exacta de la plantilla.
 * Primero las cabeceras EXACTAS y después los sinónimos, para que «Razón
 * social» sea la razón social aunque también sea un sinónimo del nombre. Un
 * campo no se empareja dos veces: gana la primera columna.
 */
export function emparejar(cabeceras: readonly string[], columnas: readonly Columna[], minima: boolean): Emparejamiento {
  const usadas = new Set<string>();
  const normales = cabeceras.map(normalizarCabecera);
  const out: (string | null)[] = normales.map(() => null);
  const pasada = (casa: (k: Columna, n: string) => boolean): void => {
    normales.forEach((n, i) => {
      if (n === "" || out[i] !== null) return;
      const col = columnas.find((k) => !usadas.has(k.key) && casa(k, n));
      if (col === undefined) return;
      usadas.add(col.key);
      out[i] = col.key;
    });
  };
  pasada((k, n) => normalizarCabecera(k.header) === n);
  if (!minima) pasada((k, n) => k.aliases.some((a) => normalizarCabecera(a) === n));
  return out;
}

/** Los campos obligatorios que no tiene ninguna columna. */
export function faltan(emparejamiento: Emparejamiento, columnas: readonly Columna[]): string[] {
  const puestos = new Set(emparejamiento.filter((x): x is string => x !== null));
  return columnas.filter((c) => c.required && !puestos.has(c.key)).map((c) => c.key);
}

const FECHAS = new Set(["issueDate", "operationDate"]);

/**
 * Una fecha escrita a la española (`30/11/2024`, `30-11-24`, `30.11.2024`) a
 * `AAAA-MM-DD`. La que ya viene así se queda. Lo que no es una fecha de verdad
 * devuelve `null` y se manda tal cual: la API dice qué le pasa.
 */
export function fechaDeTexto(v: string): string | null {
  const t = v.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  const m = t.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/);
  if (m === null) return null;
  const anio = m[3]!.length === 2 ? `20${m[3]}` : m[3]!;
  const mes = m[2]!.padStart(2, "0");
  const dia = m[1]!.padStart(2, "0");
  const d = new Date(`${anio}-${mes}-${dia}T00:00:00Z`);
  return Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== `${anio}-${mes}-${dia}` ? null : `${anio}-${mes}-${dia}`;
}

function valores(celdas: readonly string[], emparejamiento: Emparejamiento): Record<string, string> {
  const out: Record<string, string> = {};
  emparejamiento.forEach((k, i) => {
    if (k === null) return;
    const v = celdas[i] ?? "";
    out[k] = FECHAS.has(k) ? (fechaDeTexto(v) ?? v) : v;
  });
  return out;
}

/** Las facturas: una por número y fecha, con una línea por fila. Guarda qué filas forman cada una. */
export type GrupoDeFilas = { readonly item: Item; readonly filas: readonly number[] };

export function itemsDe(hoja: Hoja, emparejamiento: Emparejamiento, columnas: readonly Columna[], facturas: boolean): GrupoDeFilas[] {
  if (!facturas) {
    return hoja.filas.map((f) => ({ item: { row: f.numero, data: valores(f.celdas, emparejamiento) }, filas: [f.numero] }));
  }
  const deLinea = new Set(columnas.filter((c) => c.line === true).map((c) => c.key));
  const grupos = new Map<string, { data: Record<string, unknown>; lines: Record<string, string>[]; filas: number[]; row: number }>();
  const orden: string[] = [];
  for (const f of hoja.filas) {
    const v = valores(f.celdas, emparejamiento);
    const linea: Record<string, string> = {};
    const cabeza: Record<string, string> = {};
    for (const [k, x] of Object.entries(v)) (deLinea.has(k) ? linea : cabeza)[k] = x;
    // Sin número no se puede agrupar: va sola, y la API dirá que falta.
    const clave = (cabeza.number ?? "") === "" ? `#${f.numero}` : `${cabeza.number}|${cabeza.issueDate ?? ""}`;
    const g = grupos.get(clave);
    if (g === undefined) {
      grupos.set(clave, { data: cabeza, lines: [linea], filas: [f.numero], row: f.numero });
      orden.push(clave);
    } else {
      g.lines.push(linea);
      g.filas.push(f.numero);
    }
  }
  return orden.map((k) => {
    const g = grupos.get(k)!;
    return { item: { row: g.row, data: { ...g.data, lines: g.lines } }, filas: g.filas };
  });
}

/** En trozos que la API admite: hasta `max` filas y unos 200 KB cada uno. */
export function trozos<T>(items: readonly T[], max: number, maxBytes = 200 * 1024): T[][] {
  const out: T[][] = [];
  let actual: T[] = [];
  let bytes = 0;
  for (const it of items) {
    const b = JSON.stringify(it).length * 2;
    if (actual.length > 0 && (actual.length >= max || bytes + b > maxBytes)) {
      out.push(actual);
      actual = [];
      bytes = 0;
    }
    actual.push(it);
    bytes += b;
  }
  if (actual.length > 0) out.push(actual);
  return out;
}

/**
 * La fila del Excel de un error. En una factura, `lines.1.base` es la SEGUNDA
 * fila del grupo; lo de la factura entera, la primera.
 */
export function filaDelError(grupo: GrupoDeFilas, campo: string): number {
  const m = campo.match(/^lines\.(\d+)\./);
  return m === null ? grupo.filas[0]! : (grupo.filas[posicion(m[1]) ?? 0] ?? grupo.filas[0]!);
}

const celdaCsv = (v: string): string => (/[;"\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

/** El CSV de las filas que no entraron: la cabecera del Excel, sus celdas y el motivo. BOM y `;`, como Excel en España. */
export function csvDeErrores(hoja: Hoja, motivos: ReadonlyMap<number, string>, cabeceraMotivo: string): string {
  const lineas = [[...hoja.cabeceras, cabeceraMotivo].map(celdaCsv).join(";")];
  for (const f of hoja.filas) {
    const m = motivos.get(f.numero);
    if (m === undefined) continue;
    lineas.push([...f.celdas, m].map(celdaCsv).join(";"));
  }
  return `﻿${lineas.join("\r\n")}\r\n`;
}
